import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type LessonContentItem = {
  item_id: string;
  content_id: string;
  type: 'video' | 'article' | 'note';
  title: string;
  url: string | null;
  description: string | null;
  thumbnail_url: string | null;
  read_time_minutes: number | null;
  position: number;
  is_required: boolean;
  created_at: string;
  viewed_at: string | null;
  watched_seconds: number;
  duration_seconds: number | null;
};

export type LessonBundle = {
  lesson_id: string;
  items: LessonContentItem[];
  required_total: number;
  required_done: number;
  quiz_complete: boolean;
};

const EMPTY: LessonBundle = {
  lesson_id: '',
  items: [],
  required_total: 0,
  required_done: 0,
  quiz_complete: false,
};

/**
 * Loads a lesson as a single unified experience: its ordered content items
 * (videos / articles / resources from the coach's library) plus this player's
 * per-item progress. Coaches get the same bundle for lessons they own.
 */
export function useLessonBundle(lessonId: string | null | undefined) {
  const { user } = useAuth();
  const [bundle, setBundle] = useState<LessonBundle>({ ...EMPTY });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!user || !lessonId) {
      setBundle({ ...EMPTY });
      setLoading(false);
      return;
    }
    const { data, error } = await supabase.rpc('get_lesson_bundle' as any, { p_lesson_id: lessonId });
    if (!error && data) {
      setBundle(data as unknown as LessonBundle);
    } else {
      setBundle({ ...EMPTY, lesson_id: lessonId });
    }
    setLoading(false);
  }, [user, lessonId]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  return { bundle, loading, reload };
}

/** Lesson completion = quiz complete AND every required content item viewed. */
export function isLessonFullyComplete(bundle: LessonBundle) {
  return bundle.quiz_complete && bundle.required_done >= bundle.required_total;
}
